import { useState } from "react";
import type { MeResponse } from "@edu/shared";
import { setTracking } from "../../lib/api.ts";
import { Dialog } from "../shell/Dialog.tsx";

/**
 * Учёт посещений и оплат: включение.
 *
 * ⚠️ **Одна ручка на всех учеников** (решение владельца 21.08.2026). Не по
 * ученику, потому что это свойство рабочей привычки преподавателя: тот, кто
 * ведёт учёт, ведёт его по всем, а не по половине. Как платит конкретный ученик
 * (поурочно, помесячно, никак) настраивается в его анкете, и это другой вопрос:
 * здесь включается раздел, там описывается договорённость.
 *
 * ⚠️ **Выключено по умолчанию, и это и есть просьба «не назойливо».** Пока
 * человек сюда не зашёл, ни в карточке ученика, ни в сетке расписания не
 * появляется ни одной плитки, ни одной колонки и ни одного счётчика, а сервер
 * не делает ради них ни одного запроса.
 *
 * Окном, а не разделом настроек, по той же причине, что смена пароля: экрана
 * профиля в продукте нет, и заводить раздел ради двух галочек значит добавить в
 * сайдбар пункт, где всегда две галочки. Когда профиль появится, окно переедет
 * туда без изменений: оно не знает ни про сайдбар, ни про адрес.
 */
export function TrackingDialog(props: { me: MeResponse; onDone: (me: MeResponse) => void; onClose: () => void }) {
  const current = props.me.tracking ?? { attendance: false, payments: false };
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const toggle = (patch: { attendance?: boolean; payments?: boolean }): void => {
    void (async () => {
      setBusy(true);
      setErr(null);
      try {
        // Ответ заменяет сессию целиком: сервер присылает `MeResponse`, чтобы
        // кабинет не собирал её из двух источников
        props.onDone(await setTracking(patch));
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <Dialog
      title="Учёт занятий"
      note="настройка для всех учеников"
      onClose={props.onClose}
      foot={
        <button type="button" className="btn btn--primary" onClick={props.onClose}>
          Готово
        </button>
      }
    >
      {err !== null && <p className="err">{err}</p>}

      <div className="form">
        <label className="form__check">
          <input
            type="checkbox"
            checked={current.attendance}
            disabled={busy}
            onChange={(e) => toggle({ attendance: e.target.checked })}
          />
          Отмечать, кто был на занятии
        </label>
        <p className="hint">
          В карточке ученика и в расписании появится отметка «был», «не пришёл» или пусто.
          Пропуски считаются отдельно от оплат.
        </p>

        <label className="form__check">
          <input
            type="checkbox"
            checked={current.payments}
            disabled={busy}
            onChange={(e) => toggle({ payments: e.target.checked })}
          />
          Отмечать оплаченные занятия
        </label>
        <p className="hint">
          Отмечаете вы сами: продукт денег не видит и не принимает. Отмечать можно и занятия,
          которые прошли не в нашей комнате. Как платит каждый ученик (за занятие или раз в
          месяц) настраивается в его анкете.
        </p>
      </div>
    </Dialog>
  );
}
